import { useState, useCallback, useRef, useEffect } from 'react';
import { Col, Row } from '@amzn/stencil-react-components/layout';
import { Chip } from '@amzn/stencil-react-components/chip';
import {
  MessageBanner,
  MessageBannerType,
} from '@amzn/stencil-react-components/message-banner';
import { IconPersonSmall } from '@amzn/stencil-react-icons';
import {
  validateAssigneeAlias,
  AssigneeValidationErrorCode,
  MAX_ASSIGNEES,
  isValidAlias,
} from '../domain';
import { EmployeePicker } from './EmployeePicker';

export interface MultiAssigneePickerProps {
  /** Current list of assignee aliases (controlled). */
  readonly assignees: readonly string[];
  /** Called when a new assignee should be added. */
  readonly onAdd: (alias: string) => void;
  /** Called when an assignee should be removed (chip dismissed). */
  readonly onRemove: (alias: string) => void;
  /** Program scope for the employee-search suggest call. */
  readonly programId: string;
  /** Whether the picker is disabled (e.g. during a save). */
  readonly disabled?: boolean;
  /** Placeholder for the search input. */
  readonly placeholder?: string;
  /** Accessible label for the input. */
  readonly ariaLabel?: string;
  /** data-testid for the underlying input. */
  readonly dataTestId?: string;
  /**
   * Translated validation error messages. The component shows inline
   * banners for DUPLICATE, MAX_REACHED, and INVALID_ALIAS.
   * Callers pass translated strings so the shared component stays
   * locale-agnostic.
   */
  readonly errorMessages?: {
    duplicate?: string;
    maxReached?: string;
    invalidAlias?: string;
    required?: string;
  };
}

/**
 * Shared multi-assignee picker component.
 *
 * Combines `EmployeePicker` (with autocomplete from Orbit's
 * SuggestPeople endpoint) with:
 * - **Enter-to-commit**: if the typed text is a valid alias and no
 *   suggestion is highlighted/selected, pressing Enter commits it
 *   directly — no dropdown click required. This handles beta/dev
 *   environments where the API returns no results.
 * - **Suggestion selection**: if results ARE available, the user can
 *   click or keyboard-navigate to a suggestion and it commits on
 *   selection (standard EmployeePicker behavior).
 * - **Chip display**: current assignees render as dismissible chips
 *   above the input.
 * - **Inline validation**: DUPLICATE and MAX_REACHED errors surface
 *   as a MessageBanner below the input.
 * - **Double-add guard**: a ref-based pendingAdds set prevents the
 *   same alias from being committed twice before React re-renders.
 *
 * Use everywhere multi-assignee entry is needed:
 * - CreateAuditSheet (create form)
 * - ManageAssigneesModal (edit existing audit)
 * - Future multi-assignee forms
 */
export function MultiAssigneePicker({
  assignees,
  onAdd,
  onRemove,
  programId,
  disabled = false,
  placeholder = 'Search by alias or name',
  ariaLabel = 'Add assignee',
  dataTestId = 'multi-assignee-picker',
  errorMessages,
}: MultiAssigneePickerProps): JSX.Element {
  const [validationError, setValidationError] = useState<string | null>(null);
  const typedTextRef = useRef('');
  const pendingAddsRef = useRef<Set<string>>(new Set());

  // Clear pending adds when the prop catches up.
  useEffect(() => {
    pendingAddsRef.current.clear();
  }, [assignees]);

  const resolveError = useCallback(
    (code: AssigneeValidationErrorCode): string => {
      if (code === AssigneeValidationErrorCode.DUPLICATE)
        return errorMessages?.duplicate ?? 'This assignee has already been added';
      if (code === AssigneeValidationErrorCode.MAX_REACHED)
        return (
          errorMessages?.maxReached ?? `Maximum of ${MAX_ASSIGNEES} assignees allowed`
        );
      if (code === AssigneeValidationErrorCode.INVALID_ALIAS)
        return (
          errorMessages?.invalidAlias ??
          'Alias should not contain @ or special characters'
        );
      return errorMessages?.required ?? 'Assignee is required';
    },
    [errorMessages],
  );

  const handleAdd = useCallback(
    (alias: string) => {
      const normalized = alias.trim().toLowerCase();
      if (!normalized) return;
      // Double-fire guard.
      if (pendingAddsRef.current.has(normalized)) return;
      const currentArr = [...assignees, ...Array.from(pendingAddsRef.current)];
      const result = validateAssigneeAlias(normalized, currentArr);
      if (!result.valid) {
        setValidationError(resolveError(result.errorCode));
        return;
      }
      setValidationError(null);
      pendingAddsRef.current.add(normalized);
      onAdd(normalized);
    },
    [assignees, onAdd, resolveError],
  );

  const handlePickerSelect = useCallback(
    (login: string) => {
      handleAdd(login);
    },
    [handleAdd],
  );

  const handleTextChange = useCallback((query: string) => {
    typedTextRef.current = query;
  }, []);

  // Enter-to-commit: wraps the picker in a div with onKeyDown.
  // If the typed text is a valid alias when Enter is pressed,
  // commit it immediately (handles the "no suggestions available"
  // case in beta/dev).
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter') {
        const typed = typedTextRef.current.trim();
        if (typed && isValidAlias(typed)) {
          e.preventDefault();
          e.stopPropagation();
          handleAdd(typed);
          // Note: EmployeePicker's clearOnSelect will reset the input
          // when the selection fires. For Enter-commit (no selection
          // event), the picker still shows the typed text. The next
          // onSelect from clearOnSelect's reset will clear it, OR the
          // user types fresh text. This is acceptable UX — the chip
          // appears immediately confirming the add.
        }
      }
    },
    [handleAdd],
  );

  return (
    <Col gridGap="dimensions.spacing.200">
      {/* Assignee chips */}
      {assignees.length > 0 && (
        <Row gridGap="dimensions.spacing.200" flexWrap="wrap">
          {assignees.map((alias) => (
            <Chip
              key={alias}
              icon={<IconPersonSmall aria-hidden="true" />}
              onDismiss={() => onRemove(alias)}
              dismissButtonAltText={`Remove ${alias}`}
            >
              {alias}
            </Chip>
          ))}
        </Row>
      )}

      {/* Search input with Enter-to-commit wrapper */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div onKeyDown={handleKeyDown}>
        <EmployeePicker
          programId={programId}
          value={null}
          onSelect={handlePickerSelect}
          clearOnSelect={true}
          freeTextValidator={isValidAlias}
          onTextChange={handleTextChange}
          placeholder={placeholder}
          ariaLabel={ariaLabel}
          disabled={disabled}
          dataTestId={dataTestId}
        />
      </div>

      {/* Validation error */}
      {validationError && (
        <MessageBanner type={MessageBannerType.Error} isDismissible={false}>
          {validationError}
        </MessageBanner>
      )}
    </Col>
  );
}
